'use strict'

/* -----------------------------------------------------------------------------
 * dependencies
 * -------------------------------------------------------------------------- */

// 3rd party
import React, { useRef } from 'react'
import ReactDOM from 'react-dom'
import { Formik, useFormikContext } from 'formik'
import Icon from '@mdi/react'
import { mdiFile } from '@mdi/js'

// walrus
import {
  accessibilityLabel,
  ThemeVariantProvider
} from '@components/kit/_support'
import Box from '@components/kit/box'
import Circle from '@components/kit/circle'
import Loading from '@components/kit/loading'
import { BoxLink, BoxLinkProps } from '@components/kit/link'
import { SelectButton, SelectMenu, useSelect } from '@components/kit/select'
import Text, { TextProps } from '@components/kit/text'
import {
  CheckBoxField,
  RadioButtonField,
  RadioGroup
} from '@components/kit/form'
import MemberListRelativeTime from './member-list-relative-time'
import submitForm from '@lib/submit-form'
import FlatList from '@components/kit/flat-list'
import { colors } from '@theme'

// lib
import { ChatContext } from '../../chat/chat.web'
import MemberAvatar from '../member-avatar'
import { useChatStore } from '@store/Chat'
import { useAuthStore } from '@store/Auth'
import { ChatListFilters, ChatListItem } from '@store/Chat/types'
import SearchBar from './search-bar'
import { StatusFilterSelect } from './status-filter-select'

/* -----------------------------------------------------------------------------
 * Types
 * -------------------------------------------------------------------------- */

export type MemberListFormValues = {
  opportunity_status?: number
  professional_filter?: boolean | null
  direction_filter?: 'inbound' | 'outbound'
}

const filtersToFormValues = (
  filters: ChatListFilters
): MemberListFormValues => ({
  opportunity_status: filters.category,
  professional_filter: filters.matched_to_me ? true : null,
  direction_filter:
    filters.last_message_direction === 'inbound' ||
    filters.last_message_direction === 'outbound'
      ? filters.last_message_direction
      : undefined
})

/* -----------------------------------------------------------------------------
 * PriorityLabel
 * -------------------------------------------------------------------------- */

export type PriorityLabelProps = Omit<TextProps, 'children'> & {
  priority: 'high' | 'medium' | 'low'
}

export const PriorityLabel = ({ priority, ...rest }: PriorityLabelProps) => {
  const colors = {
    high: 'darkGreen',
    medium: 'orange',
    low: 'shade04'
  } as const

  return (
    <Text variant='badge' textColor={colors[priority]} {...rest}>
      {priority} Priority
    </Text>
  )
}

/* -----------------------------------------------------------------------------
 * UnreadBadge
 *
 * The unread count the old kit's NotificationBadge drew on the avatar.
 * -------------------------------------------------------------------------- */

const UnreadBadge = ({ count }: { count: string }) => (
  <Circle
    size={16}
    width='auto'
    minWidth={16}
    bg='red'
    position='absolute'
    top={-5}
    right={-5}
  >
    <Text
      px='04'
      variant='label'
      textColor='white'
      fontSize={8}
      lineHeight={10}
    >
      {count}
    </Text>
  </Circle>
)

/* -----------------------------------------------------------------------------
 * MemberListItem
 * -------------------------------------------------------------------------- */

export type MemberListItemProps = Partial<BoxLinkProps> & {
  item: ChatListItem
}

export const MemberListItem = React.memo(
  ({ item, ...rest }: MemberListItemProps) => {
    const { userId: activeUserId } = React.useContext(ChatContext)
    const isActive = activeUserId === item.userId
    const showUnread =
      typeof item.unreadCount === 'number' && item.unreadCount > 0
    const showPriority = Boolean(item.priority)
    const hasPreview = Boolean(item.previewText && item.previewCreated)
    const displayName = (item.preferredName || item.name || '').trim()

    return (
      <BoxLink
        {...accessibilityLabel('Member List Item')}
        position='relative'
        p='16'
        borderBottomWidth={1}
        borderColor='shade03'
        bg={isActive ? 'shade01' : 'white'}
        path={`/chat/${item.userId}`}
        {...rest}
      >
        <Box flexDirection='row' alignItems='center'>
          <Box flex='0 0 auto'>
            <MemberAvatar
              name={displayName}
              {...(item.online
                ? {
                    borderRadius: 'pill',
                    borderWidth: 2,
                    borderColor: 'walrusGreen500',

                    style: {
                      boxShadow: '0px 0px 10px 5px rgba(195, 238, 230, 0.6)'
                    } as Record<string, string>
                  }
                : {})}
            />
            {showUnread && !isActive && (
              <UnreadBadge
                count={
                  item.unreadCount! > 9 ? '9+' : item.unreadCount!.toString()
                }
              />
            )}
          </Box>
          <Box flex='1 1 auto' pl='08'>
            <Box
              flexDirection='row'
              alignItems='center'
              justifyContent='space-between'
            >
              <Text
                {...accessibilityLabel('Member Item Name')}
                variant='subtitle'
                truncate
              >
                {displayName}
              </Text>
              {showPriority && (
                <PriorityLabel priority={item.priority!} truncate />
              )}
            </Box>
            {item.preferredName ? (
              <Text variant='meta' textColor='shade05' truncate>
                {item.name}
              </Text>
            ) : null}
            {item.subtitle ? <Text variant='meta'>{item.subtitle}</Text> : null}
          </Box>
        </Box>
        {hasPreview ? (
          <Box pt='08' flexDirection='row' alignItems='flex-end'>
            <Box flex='1 1 auto' flexDirection='row' alignItems='center'>
              {item.isFilePreview && (
                <Box mr='04' style={{ flexShrink: 0 }}>
                  <Icon
                    path={mdiFile}
                    size='14px'
                    color={
                      (colors as Record<string, string>).shade06 ?? '#4B5563'
                    }
                  />
                </Box>
              )}
              <Text variant='small' flex='1 1 auto' numberOfLines={2}>
                {!item.isFilePreview && (
                  <Text variant='small' fontWeight='bold'>{`${
                    item.previewOwnerName || 'Unknown'
                  }: `}</Text>
                )}
                {item.previewText}
              </Text>
            </Box>
            <MemberListRelativeTime
              flex='0 0 auto'
              pl='08'
              date={item.previewCreated}
            />
          </Box>
        ) : (
          <Box height={48} />
        )}
        {isActive && (
          <Box
            position='absolute'
            top={0}
            bottom={0}
            right={0}
            width={2}
            bg='red'
          />
        )}
      </BoxLink>
    )
  },
  (prevProps, nextProps) => prevProps.item.userId === nextProps.item.userId
)

/* -----------------------------------------------------------------------------
 * MatchedPharmacistFitler
 * -------------------------------------------------------------------------- */

const MatchedPharmacistFitler = () => {
  const { handleSubmit } = useFormikContext<MemberListFormValues>()

  return (
    <Box
      py='12'
      px='12'
      borderBottomWidth={1}
      borderColor='shade03'
      width='50%'
    >
      <CheckBoxField
        label='Matched to me'
        name='professional_filter'
        textColor='purple'
        fontWeight='700'
        variant='small'
        onValueChange={() => handleSubmit()}
      />
    </Box>
  )
}

/* -----------------------------------------------------------------------------
 * MessageDirectionFilter
 * -------------------------------------------------------------------------- */

const directionToTextMap = {
  none: 'All Messages',
  inbound: 'Inbound',
  outbound: 'Outbound'
} as const

const MessageDirectionFilter = () => {
  const { values, handleSubmit } = useFormikContext<MemberListFormValues>()
  const buttonRef = useRef(null)
  const menuRef = useRef(null)
  const items = Object.entries(directionToTextMap)
  const { getToggleButtonProps, getMenuProps, closeMenu } = useSelect({
    buttonRef,
    menuRef,
    items
  })
  const direction = values.direction_filter ?? 'none'

  const handleSelectItem = () => {
    closeMenu()
    handleSubmit()
  }

  return (
    <>
      <Box
        ref={buttonRef}
        width='50%'
        height='45px'
        justifyContent='center'
        borderBottomWidth={1}
        borderColor='shade03'
      >
        <ThemeVariantProvider value='punchThroughPurple'>
          <SelectButton {...getToggleButtonProps()}>
            <Text fontWeight='700'>{directionToTextMap[direction]}</Text>
          </SelectButton>
        </ThemeVariantProvider>
      </Box>
      {ReactDOM.createPortal(
        <SelectMenu ref={menuRef} {...getMenuProps()} minWidth='144px'>
          <RadioGroup>
            {items.map(item => (
              <Box mt='12' key={item[0]}>
                <RadioButtonField
                  label={item[1]}
                  name='direction_filter'
                  value={item[0] === 'none' ? undefined : item[0]}
                  onValueChange={handleSelectItem}
                />
              </Box>
            ))}
          </RadioGroup>
        </SelectMenu>,
        document.getElementById('root')
      )}
    </>
  )
}

const isValidChatListItem = (item: ChatListItem): boolean =>
  Number.isFinite(item.userId) && Number.isFinite(item.roomId)

/* -----------------------------------------------------------------------------
 * ItemsList
 * -------------------------------------------------------------------------- */

const ItemsList = () => {
  const professionalId = useAuthStore(state => state.userId)
  const { isSubmitting } = useFormikContext<MemberListFormValues>()
  const memberList = useChatStore(state => state.memberList)
  const { loadMoreMemberList } = useChatStore.getState()

  const listItems = memberList.items.filter(isValidChatListItem)
  const isLoadingMore = memberList.isLoading && listItems.length > 0

  const handleEndReached = () => {
    if (!memberList.hasNextPage || memberList.isLoading) {
      return
    }
    loadMoreMemberList({ professionalUserId: professionalId })
  }

  return (
    <>
      {!isSubmitting ? (
        <FlatList
          {...accessibilityLabel('Member Preview')}
          data={listItems}
          initialNumToRender={10}
          keyExtractor={(item: ChatListItem) => String(item.userId)}
          renderItem={({ item }) => (
            <MemberListItem item={item as ChatListItem} />
          )}
          onEndReached={handleEndReached}
          onEndReachedThreshold={0.4}
          ListFooterComponent={
            isLoadingMore ? (
              <Box py='16'>
                <Loading size='small' />
              </Box>
            ) : null
          }
        />
      ) : (
        <Loading mt='48' />
      )}
    </>
  )
}

/* -----------------------------------------------------------------------------
 * MemberList
 * -------------------------------------------------------------------------- */

const MemberList = () => {
  const professionalId = useAuthStore(state => state.userId)
  const memberList = useChatStore(state => state.memberList)
  const { setMemberListFilters } = useChatStore.getState()

  const isInitialLoad = memberList.isLoading && memberList.items.length === 0

  const handleFormSubmit = submitForm(async (values: MemberListFormValues) => {
    const { professional_filter, opportunity_status, direction_filter } = values

    await setMemberListFilters(
      {
        category: opportunity_status ?? 0,
        matched_to_me: Boolean(professional_filter),
        last_message_direction: direction_filter ?? ''
      },
      { professionalUserId: professionalId }
    )
  })

  const handleSearchMembers = async (q: string) => {
    await setMemberListFilters(
      { search: q },
      { professionalUserId: professionalId }
    )
  }

  if (isInitialLoad) {
    return <Loading mt='48' />
  }

  return (
    <Formik<MemberListFormValues>
      enableReinitialize
      initialValues={filtersToFormValues(memberList.filters)}
      onSubmit={handleFormSubmit}
    >
      <Box {...accessibilityLabel('Member List')} flex='1 1 auto'>
        <Box>
          <StatusFilterSelect />
        </Box>
        <Box borderBottomWidth={1} borderTopWidth={1} borderColor='shade03'>
          <SearchBar
            initialQuery={memberList.filters.search}
            onPress={handleSearchMembers}
          />
        </Box>
        <Box flexDirection='row' alignItems='center'>
          <MatchedPharmacistFitler />
          <MessageDirectionFilter />
        </Box>
        <ItemsList />
      </Box>
    </Formik>
  )
}

export default MemberList
